/**
 * US-state consent-law data + a tiny detector.
 *
 * Guesses only when browser timezone maps unambiguously to a single state
 * (Detroit → MI, Phoenix → AZ, Honolulu → HI, Anchorage → AK). Anything
 * ambiguous (America/Los_Angeles could be CA/OR/WA/NV, half all-party,
 * half not) is intentionally not guessed. Explicit ?state=XX in the URL
 * always wins.
 */

const ALL_PARTY = new Set([
  "CA", "CT", "DE", "FL", "IL", "MD", "MA", "MI",
  "MT", "NV", "NH", "PA", "WA",
]);

const STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas",
  CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
  DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii",
  ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas",
  KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
  MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
  VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia",
  WI: "Wisconsin", WY: "Wyoming",
};

const TZ_UNAMBIGUOUS = {
  "America/Phoenix": "AZ",
  "Pacific/Honolulu": "HI",
  "America/Anchorage": "AK",
  "America/Detroit": "MI",
  "America/Puerto_Rico": "PR",
};

function normalize(code) {
  if (!code || typeof code !== "string") return null;
  const up = code.toUpperCase();
  return STATES[up] ? up : null;
}

function guessFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TZ_UNAMBIGUOUS[tz] || null;
  } catch (_e) {
    return null;
  }
}

/**
 * @returns null | { code, name, isAllParty, source, guessed }
 *   source = 'url' when ?state=XX was used; 'timezone' when guessed
 */
export function detectVisitorState() {
  if (typeof window === "undefined") return null;

  // 1. Explicit URL param wins.
  try {
    const raw = new URLSearchParams(window.location.search).get("state");
    const code = normalize(raw);
    if (code) {
      return {
        code,
        name: STATES[code],
        isAllParty: ALL_PARTY.has(code),
        source: "url",
        guessed: false,
      };
    }
  } catch (_e) {
    /* ignore */
  }

  // 2. Unambiguous timezone guess (fallback).
  const guess = guessFromTimezone();
  if (guess) {
    return {
      code: guess,
      name: STATES[guess],
      isAllParty: ALL_PARTY.has(guess),
      source: "timezone",
      guessed: true,
    };
  }
  return null;
}

/**
 * RCFP is one of the most cited resources on US recording law. Single URL
 * for all states, the visitor jumps to their state on the landing page.
 */
export const RCFP_URL = "https://www.rcfp.org/reporters-recording-guide/";
